// IndexedDBStorage.ts
// IndexedDB優先・localStorageから自動引き継ぎ付きバージョン

type KV = { key: string; value: string };
const DEFAULT_DB = "LingualNinja_DB";
const DEFAULT_STORE = "kv";

export class IndexedDBStorage {
    private static _instance: IndexedDBStorage | null = null;
    private dbPromise: Promise<IDBDatabase> | null = null;

    private constructor() {}

    static getInstance(): IndexedDBStorage {
        if (!this._instance) {
            this._instance = new IndexedDBStorage();
        }
        return this._instance;
    }

    /** IndexedDB接続（初回のみopen、それ以降はキャッシュ） */
    private async getDB(): Promise<IDBDatabase> {
        if (this.dbPromise) return this.dbPromise;

        this.dbPromise = new Promise((resolve, reject) => {
            const req = indexedDB.open(DEFAULT_DB, 1);
            req.onupgradeneeded = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains(DEFAULT_STORE)) {
                    db.createObjectStore(DEFAULT_STORE, { keyPath: "key" });
                }
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });

        return this.dbPromise;
    }

    /** IndexedDB→localStorage フェールオーバー付き読み込み */
    async getItemAsync(key: string): Promise<string | null> {
        const db = await this.getDB();

        // ① IndexedDBから試す
        const fromIDB: string | null = await new Promise((resolve, reject) => {
            const tx = db.transaction(DEFAULT_STORE, "readonly");
            const os = tx.objectStore(DEFAULT_STORE);
            const req = os.get(key);
            req.onsuccess = () =>
                resolve((req.result as KV | undefined)?.value ?? null);
            req.onerror = () => reject(req.error);
        });

        if (fromIDB !== null) {
            return fromIDB;
        }

        // ② localStorage からフェールオーバー
        try {
            const fromLocal = window.localStorage.getItem(key);
            if (fromLocal !== null) {
                // IndexedDB にも保存
                await this.setItemAsync(key, fromLocal);
                // localStorage 側は削除して移行完了
                window.localStorage.removeItem(key);
                return fromLocal;
            }
        } catch {
            // localStorage 使用不可 (Safari プライベートモードなど) → 無視
        }

        // ③ 両方になければ null
        return null;
    }

    async setItemAsync(key: string, value: string): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(DEFAULT_STORE, "readwrite");
            const os = tx.objectStore(DEFAULT_STORE);
            const req = os.put({ key, value });
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    async removeItemAsync(key: string): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(DEFAULT_STORE, "readwrite");
            const os = tx.objectStore(DEFAULT_STORE);
            const req = os.delete(key);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    async clearAsync(): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(DEFAULT_STORE, "readwrite");
            const os = tx.objectStore(DEFAULT_STORE);
            const req = os.clear();
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }
}

// 共通インスタンス
export const indexedDBStorage = IndexedDBStorage.getInstance();

